import React, { useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  Fab,
  DialogContent,
  Button,
  CircularProgress,
  Box,
} from "@material-ui/core";
import { DatePicker } from "@material-ui/pickers";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { Skeleton } from "@material-ui/lab";
import { IVehicleAvailability } from ".";
import { DateTime as d } from "luxon";
import { TestDriveTypes } from "../../../../../reducers/testDriveBooking/types";

interface IProps {
  dialogOpen: boolean;
  bookingType: string;
  loading: boolean;
  vehicleAvailabilityPickUpSlots: IVehicleAvailability[]
  vehicleAvailabilityDropOffSlots: IVehicleAvailability[];
  handleDialogState: () => void;
  fetchAvailability: any;
  updateSlot: (data: string) => void;
  updateSelectedDate: (selectedDate: string) => void;
  setPickupSlotData?: (
    driverId: string,
    slotId: string,
    slotTime: string
  ) => void;
  setDropOffSlotData?: (
    driverId: string,
    slotId: string,
    slotTime: string
  ) => void;
}

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "row",
    [theme.breakpoints.up("md")]: {
      flexDirection: "column",
    },
    gap: theme.spacing(2),
  },
  selectedTimeSlot: {
    maxWidth: "100px",
    minWidth: "100px",
    "& .MuiChip-clickable:focus": {
      backgroundColor: "var(--primary-color)",
    },
    backgroundColor: "var(--primary-color)",
  },
  proceedButton: {
    marginBottom: theme.spacing(0.5),
    borderRadius: 0,
  },
  proceedIcon: {
    marginRight: theme.spacing(1),
  },
  listItem: {
    maxWidth: "100px",
  },
  timeSlotContainer: {
    maxHeight: "250px",
    overflow: "auto",
  },
  root: {
    maxWidth: 600,
    margin: "auto",
  },
  title: {
    marginBottom: theme.spacing(2),
  },
  timeSlot: {
    marginBottom: theme.spacing(0.5),
  },
  selected: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  btnContainer: {
    minWidth: "120px",
  },
  calendarContainer: {
    minWidth: "310px",
  },
  slotsContainer: {
    minWidth: "400px",
  },
  dialog: {
    "& .MuiDialog-container": {
      "& .MuiPaper-root": {
        width: "100%",
        maxWidth: "760px",
      },
    },
  },
}));

export const CalendarTimeSlotSelector: React.FC<IProps> = ({
  dialogOpen,
  bookingType,
  loading,
  vehicleAvailabilityDropOffSlots,
  vehicleAvailabilityPickUpSlots,
  handleDialogState,
  fetchAvailability,
  updateSlot,
  updateSelectedDate,
  setPickupSlotData,
  setDropOffSlotData
}) => {
  const classes = useStyles();
  const [selectedDate, setSelectedDate] = useState<string>(
    d.now().toUTC().toISO()
  );
  const [disableSlotSelector, setSlotSelectorStatus] = useState<boolean>(false);
  const [selectedDropOffSlot, setSelectedDropOffSlot] = useState<number>(0);
  const [selectedPickUpSlot, setSelectedPickupSlot] = useState<number>(0);
  const [isDriverAvailableForPickup, setIsDriverAvailableForPickUp] = useState<boolean>(false)
  const [isDriverAvailableForDropOff, setIsDriverAvailableForDropOff] = useState<boolean>(false)


  const handleDateChange = (date: MaterialUiPickersDate) => {
    setSelectedDate(date?.toISOString() || "");
    updateSelectedDate(date?.toISOString() || "")
    fetchAvailability(date?.toISOString() || "");
    setSelectedDropOffSlot(0);
    setSelectedPickupSlot(0);
  };

  const handleSelect = () => {
    handleDialogState();
  }


  const handleDropOffSlotSelection = (
    slot: IVehicleAvailability,
    selectedSlotIndex: number
  ) => {
    if (setDropOffSlotData) {
      setDropOffSlotData(slot.driverId, slot.slotId, slot.timeSlot);
    }
    const updatedSlot = bookingType === TestDriveTypes.STANDARD ?
      `${d
        .fromISO(selectedDate)
        .toLocaleString(d.DATE_FULL)} | ${slot.timeSlot}` :
      `${d
        .fromISO(selectedDate)
        .toLocaleString(d.DATE_FULL)} | ${vehicleAvailabilityPickUpSlots[selectedPickUpSlot].timeSlot} | ${slot.timeSlot}`

    updateSlot(updatedSlot)
    setSelectedDropOffSlot(selectedSlotIndex);
  };

  const handlePickUpSlotSelection = (
    slot: IVehicleAvailability,
    selectedSlotIndex: number
  ) => {
    if (setPickupSlotData) {
      setPickupSlotData(slot.driverId, slot.slotId, slot.timeSlot);
    }
    updateSlot(`${d
      .fromISO(selectedDate)
      .toLocaleString(d.DATE_FULL)} | ${slot.timeSlot} | ${vehicleAvailabilityDropOffSlots[selectedSlotIndex].timeSlot}`)

    setSelectedPickupSlot(selectedSlotIndex);
  };

  return (
    <Dialog
      onClose={handleDialogState}
      aria-labelledby="slot-selector"
      open={dialogOpen}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle>
        <Typography variant={"h3"}>Select Date and Time Slot</Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container xs={12} spacing={2}>
          <Grid item xs={4}>
            <DatePicker
              disableToolbar
              variant="static"
              disablePast
              value={selectedDate}
              onChange={handleDateChange}
            />
          </Grid>
          <Grid item xs={8}>
            <Grid
              container
              alignContent="center"
              justify="space-between"
              alignItems="center"
            >
              <Grid container className={classes.slotsContainer} spacing={2}>
                {loading && (
                  <Grid container item xs={12} spacing={1}>
                    <CircularProgress />
                  </Grid>
                )}

                {!loading && vehicleAvailabilityDropOffSlots ? (
                  <Grid item xs={6}>
                    <Grid item xs={12}><Typography variant={"subtitle1"}>Select Drop-off Slot</Typography></Grid>
                    <Grid item xs={12}><Box mt={1}></Box></Grid>
                    <Grid container spacing={2}>
                      {vehicleAvailabilityDropOffSlots.map((slot, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={2}
                          key={index}
                          className={classes.btnContainer}
                        >
                          <Button
                            variant="outlined"
                            fullWidth
                            className={`${classes.timeSlot} ${selectedDropOffSlot === index ? classes.selected : ""
                              }`}
                            onClick={() => handleDropOffSlotSelection(slot, index)}
                          >
                            {slot.timeSlot}
                          </Button>
                        </Grid>
                      ))}
                      {
                        !isDriverAvailableForDropOff && (
                          <Grid item xs={12}><Typography><strong>Note:</strong> Driver is not available please contact to rental operator.</Typography></Grid>
                        )
                      }
                    </Grid>
                  </Grid>
                ) : <></>}
                {!loading && vehicleAvailabilityPickUpSlots.length ? (
                  <Grid item xs={6}>
                    <Grid item xs={12}><Typography variant={"subtitle1"}>Select pick-up Slot</Typography></Grid>
                    <Grid item xs={12}><Box mt={1}></Box></Grid>
                    <Grid container spacing={2}>
                      {vehicleAvailabilityPickUpSlots.map((slot, index) => (
                        <Grid
                          item
                          xs={12}
                          sm={2}
                          key={index}
                          className={classes.btnContainer}
                        >
                          <Button
                            variant="outlined"
                            fullWidth
                            className={`${classes.timeSlot} ${selectedPickUpSlot === index ? classes.selected : ""
                              }`}
                            onClick={() => handlePickUpSlotSelection(slot, index)}
                          >
                            {slot.timeSlot}
                          </Button>
                        </Grid>
                      ))}
                      {
                        !isDriverAvailableForPickup && (
                          <Grid item xs={12}><Typography><strong>Note:</strong> Driver is not available please contact to rental operator.</Typography></Grid>
                        )
                      }
                    </Grid>
                  </Grid>
                ) : <></>}
                {!loading && !vehicleAvailabilityDropOffSlots.length && (
                  <Grid container item xs={12} spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="body1" style={{ marginTop: "16px" }}>
                        Vehicle Not Available for Selected Date.
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="body1" style={{ marginTop: "16px" }}>
                        Please Select a Different Date.
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <Grid container justify="center" style={{ marginTop: "20px" }}>
          <Fab
            aria-label="proceed-with-vehicle-selection"
            variant="extended"
            size="medium"
            className={classes.proceedButton}
            onClick={handleSelect}
            style={{
              backgroundColor: disableSlotSelector
                ? "#e0e0e0"
                : "var(--primary-color)",
            }}
            disabled={disableSlotSelector}
          >
            Select Slot
          </Fab>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};
